import { useState, useMemo, useRef } from 'react';

import { TooltipOptions } from './types';

import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  arrow,
  useHover,
  useClick,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
} from '@floating-ui/react';

export function useTooltip({
  event = 'hover',
  initialOpen = false,
  placement = 'bottom',
  open: controlledOpen,
  onOpenChange: setControlledOpen,
}: TooltipOptions = {}) {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(initialOpen);

  const open = controlledOpen ?? uncontrolledOpen;
  const setOpen = setControlledOpen ?? setUncontrolledOpen;

  const arrowRef = useRef(null);

  const data = useFloating({
    placement,
    open,
    onOpenChange: setOpen,
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(8),
      flip({
        crossAxis: placement.includes('-'),
        fallbackAxisSideDirection: 'start',
        padding: 5,
      }),
      shift({ padding: 5 }),
      arrow({
        element: arrowRef,
      }),
    ],
  });

  const context = data.context;

  const hover = useHover(context, {
    enabled: event === 'hover',
    move: false,
  });

  const focus = useFocus(context, {
    enabled: event === 'hover',
  });

  const click = useClick(context, {
    enabled: event === 'click',
  });

  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const interactions = useInteractions([click, hover, focus, dismiss, role]);

  return useMemo(
    () => ({
      open,
      setOpen,
      arrowRef,
      targetRef: data.refs.setReference,
      ...interactions,
      ...data,
    }),
    [open, setOpen, interactions, data]
  );
}
